/**
 * Katana Questing API - Krates (XP quests) metadata and monthly sweepstakes.
 *
 * External API (app.katana.network ecosystem), not part of the perps exchange API.
 * Allowed in CSP connect-src, see `server/cspConfig.js`.
 * NOTE: The API must also allow our origins via CORS, otherwise requests
 * are blocked by the browser and consumers should fall back to static copy.
 */

import {
  QUESTS_KRATES_METADATA_URL,
  QUESTS_SWEEPSTAKES_URL,
} from '#/config/constants';

// Reward tiers - returned by the API but not used by us (yet)
// type QuestsKrateTier = {
//   /** E.g. 'COMMON' | 'UNCOMMON' | 'RARE' | 'SUPER_RARE' */
//   rarity: string;
//   probability: number;
//   count: number;
//   rewardAvKatMin: number;
//   rewardAvKatMax: number;
//   rewardXpMin: number;
//   rewardXpMax: number;
//   rewardUsdcMin: number;
//   rewardUsdcMax: number;
// };

type QuestsKratesMetadata = {
  /** XP price to redeem (open) one Krate */
  priceXp: number;

  // Returned by the API but not used by us (yet):
  // /** ISO date, e.g. '2026-08-01' */
  // monthStart: string;
  // totalSupply: number;
  // maxPurchase: number;
  // supplyExhausted: boolean;
  // tiers: QuestsKrateTier[];
};

// Individual prize - returned by the API but not used by us (yet)
// type QuestsSweepstakesPrize = {
//   rank: number;
//   prizeUsd: number;
//   /** Shortened winner address, e.g. '0x4d60...e830'; null until the month is drawn */
//   address: string | null;
//   totalXp: number | null;
// };

type QuestsSweepstakes = {
  /** Total USD prize pool of the month (sum of all prizes) */
  prizeUsd: number;

  // Returned by the API but not used by us (yet):
  // /** 'YYYY-MM', e.g. '2026-09' */
  // month: string;
  // prizes: QuestsSweepstakesPrize[];
};

const hasFiniteNumber = (value: object, key: string): boolean => {
  const field: unknown = (value as Record<string, unknown>)[key];
  return typeof field === 'number' && Number.isFinite(field);
};

/**
 * Runtime validation - external API we do not control, so make sure the
 * response has the structure we rely on before using it.
 */
const isQuestsKratesMetadata = (
  value: unknown
): value is QuestsKratesMetadata =>
  typeof value === 'object' &&
  value !== null &&
  hasFiniteNumber(value, 'priceXp');

const isQuestsSweepstakes = (value: unknown): value is QuestsSweepstakes =>
  typeof value === 'object' &&
  value !== null &&
  hasFiniteNumber(value, 'prizeUsd');

const fetchQuestingApi = async <T>(
  url: string,
  isValid: (value: unknown) => value is T,
  label: string
): Promise<T> => {
  // Keep this a bare GET without custom headers: adding e.g.
  // `Content-Type: application/json` turns it into a preflighted request,
  // and the questing API does not handle OPTIONS (404) - a simple request
  // avoids the preflight round-trip entirely.
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load ${label} (${response.status})`);
  }
  const data: unknown = await response.json();
  if (!isValid(data)) {
    throw new Error(`Unexpected ${label} response structure`);
  }
  return data;
};

export const fetchQuestsKratesMetadata = (): Promise<QuestsKratesMetadata> =>
  fetchQuestingApi(
    QUESTS_KRATES_METADATA_URL,
    isQuestsKratesMetadata,
    'Krates metadata'
  );

/**
 * Monthly sweepstakes - USD prizes for the current month.
 * The API also accepts a `?month=YYYY-MM` query parameter to get a particular
 * month (e.g. previous winners) - not needed by us (yet).
 */
export const fetchQuestsSweepstakes = (): Promise<QuestsSweepstakes> =>
  fetchQuestingApi(QUESTS_SWEEPSTAKES_URL, isQuestsSweepstakes, 'sweepstakes');
